import React, { ContextType, useMemo } from "react";

import { Box, ListItem, ListSubheader, Typography } from "@material-ui/core";
import * as R from "ramda";

import DesktopContainerDrawer from "containers/DesktopContainerDrawer";
import MenuGroupItem from "containers/MenuGroupItem";
import MenuGroupContext from "contexts/MenuGroupContext";
import makeStyles from "hooks/makeStyles";
import useLocalize from "hooks/useLocalize";
import useMenu from "hooks/useMenu";

const useStyles = makeStyles(theme => ({
	root: {
		margin: `0 0 ${theme.spacing(1)}px 0`,
		// margin: 0,
		padding: 0,
		[theme.breakpoints.up(480)]: {
			display: "flex",
			flexDirection: "column",
			flexWrap: "wrap",
			flex: 1
		},
		[theme.breakpoints.up(769)]: {
			display: "flex",
			flexDirection: "row",
			flexWrap: "wrap",
			flex: 1
		}
	},
	header: {
		backgroundColor: theme.palette.background.default,
		position: "sticky",
		elevation: 10,
		display: "flex",
		flexDirection: "column",
		padding: 0,
		width: "100%",
		// marginLeft: -14,
		margin: 0,
		marginBottom: theme.spacing(1),
		paddingTop: theme.spacing(2),
		paddingLeft: theme.spacing(2),
		paddingRight: theme.spacing(2),
		paddingBottom: theme.spacing(2),
		minHeight: theme.spacing(6),
		justifyContent: "center",
		justifyItems: "center",
		[theme.breakpoints.up(480)]: {
			top: -4,
			flex: "1 1 100%",
			paddingLeft: theme.spacing(3),
			width: "auto"
		}
	},
	title: {
		fontSize: 24,
		fontWeight: 600,
		color: "#30323b",
		[theme.breakpoints.up(480)]: {
			fontSize: 28,
			fontWeight: 500
		}
	},
	anchorContainer: {
		position: "relative"
	},
	anchor: {
		position: "absolute",
		[theme.breakpoints.down(480)]: {
			top: -177
		}
	},
	desc: {
		lineHeight: "auto",
		fontWeight: 400,
		fontSize: 14,
		color: "#646464",
		marginTop: theme.spacing(0.5),
		[theme.breakpoints.up(480)]: {}
	},
	placeholderBox: {
		[theme.breakpoints.up(480)]: {},
		[theme.breakpoints.up(769)]: {
			flex: "1 0 50%",
			padding: "8px",
			display: "block"
		}
	}
}));

interface Props {
	menuGroupSlug: string;
}

// TODO: react types are missing a.name prop
const A = (props: Record<string, unknown>) => <a {...props} />;

const MenuGroup = ({ menuGroupSlug }: Props) => {
	const classes = useStyles();
	const { localize } = useLocalize();
	const { menuGroupBySlug } = useMenu();

	const group = menuGroupBySlug[menuGroupSlug];

	const menuGroupContext = useMemo<ContextType<typeof MenuGroupContext>>(
		() => ({
			group,
			menuItemById: R.fromPairs(group.menuItems.map(x => [x.id, x]))
		}),
		[group]
	);

	return (
		<MenuGroupContext.Provider value={menuGroupContext}>
			<Box className={classes.anchorContainer}>
				<A name={group.slug} className={classes.anchor} />
			</Box>
			<ul className={classes.root}>
				<ListSubheader className={classes.header}>
					<Typography className={classes.title} variant="h5">
						{localize(group.localeNames, group.name)}
					</Typography>
					{group.description && <Typography className={classes.desc}>{group.description}</Typography>}
				</ListSubheader>
				{group.menuItems.map(item => (
					<MenuGroupItem key={item.id} menuItemId={item.id} />
				))}
				<DesktopContainerDrawer>
					{group.menuItems.length % 2 === 1 ? (
						<ListItem key={group.slug} className={classes.placeholderBox}></ListItem>
					) : null}
				</DesktopContainerDrawer>
			</ul>
		</MenuGroupContext.Provider>
	);
};

export default MenuGroup;
